<template>
  <div class="nas-storage-page">
    <div class="nas-hero">
      <h1 class="nas-hero-title">存储</h1>
      <p class="nas-hero-lead">{{ storageFamilyIntro }}</p>
    </div>

    <div class="nas-layout">
      <main class="nas-main">
        <section class="nas-featured" :id="ANCHOR_INTRO">
          <div class="nas-featured-inner">
            <h2 class="nas-featured-title">{{ storageFeatured.title }}</h2>
            <p class="nas-featured-sub">{{ storageFeatured.subtitle }}</p>
            <p class="nas-featured-desc">{{ storageFeatured.description }}</p>
          </div>
        </section>

        <p class="nas-aux-link">
          <a href="#" @click.prevent="scrollTo(ANCHOR_INTRO)">了解存储产品族</a>
        </p>

        <section
          v-for="row in storageRows"
          :key="row.label"
          class="nas-row-block"
        >
          <!-- SmartX 式：左侧类目与右侧标题同一行 -->
          <div class="nas-row-category">{{ row.label }}</div>
          <div class="nas-row-body">
            <template v-if="!row.groupTitle && row.items[0]">
              <router-link
                :to="row.items[0].path"
                class="nas-row-head-link"
              >
                <span class="nas-row-head-name">{{ row.items[0].name }}</span>
                <p class="nas-row-head-desc">{{ row.items[0].description }}</p>
              </router-link>
            </template>
            <template v-else>
              <div class="nas-row-head-static">
                <span class="nas-row-head-name">{{ row.groupTitle }}</span>
                <p class="nas-row-head-desc">{{ row.groupTagline }}</p>
              </div>
            </template>

            <div
              v-if="row.groupTitle"
              class="nas-product-list"
            >
              <div
                v-for="item in row.items"
                :key="item.slug"
                class="nas-product-row"
              >
                <div
                  class="nas-product-main"
                  :class="{ 'has-subbox': item.subItems && item.subItems.length }"
                >
                  <div class="nas-product-block">
                    <router-link :to="item.path" class="nas-product-link">
                      <h3 class="nas-product-name">{{ item.name }}</h3>
                      <p class="nas-product-desc">{{ item.description }}</p>
                    </router-link>
                  </div>
                  <div
                    v-if="item.subItems && item.subItems.length"
                    class="nas-product-subbox"
                  >
                    <div
                      v-for="(sub, si) in item.subItems"
                      :key="si"
                      class="nas-subitem"
                    >
                      <span class="nas-subitem-name">{{ sub.name }}</span>
                      <span v-if="sub.hint" class="nas-subitem-hint">{{ sub.hint }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <aside class="nas-aside">
        <div class="nas-aside-card">
          <div class="nas-aside-badge">资料</div>
          <h3 class="nas-aside-title">技术白皮书与产品介绍</h3>
          <p class="nas-aside-text">
            机架式产品要点来自 XC 系列技术白皮书；软件专题页完整呈现《存储-软件产品介绍》PPT 原文整理。
          </p>
          <ul class="nas-aside-list">
            <li>企业级 XC-3000 系列全闪 NAS 存储技术白皮书</li>
            <li>机架式 XC-1000 系列 NAS 存储技术白皮书</li>
            <li>企业级 XC-2000 系列 NAS 存储技术白皮书</li>
            <li>桌面 XC-08 信创系列 NAS 存储技术白皮书</li>
            <li>存储-软件产品介绍（芯存 XNAS）</li>
          </ul>
          <router-link
            to="/product/storage/software"
            class="nas-aside-cta"
          >查看芯存 XNAS 软件→</router-link>
        </div>
      </aside>
    </div>


  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import {
  storageRows,
  storageFeatured,
  storageFamilyIntro,
} from '@/data/nasStorageContent.js';

const ANCHOR_INTRO = 'xnas-family';

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

onMounted(() => {
  document.title = '存储产品 | 瑞和云图';
});
</script>

<style scoped>
/* 对齐 SmartX HCI 核心产品区排版与色板，参考 https://www.smartx.com/smartx-hci/ */
.nas-storage-page {
  --nas-h1: clamp(32px, 3.6vw, 42px);
  --nas-lead: 15px;
  --nas-body: 14px;
  --nas-small: 13px;
  --nas-caption: 12px;
  --nas-ink: #1a1a1a;
  --nas-ink-soft: #374151;
  --nas-muted: #6b7280;
  --nas-label: #9ca3af;
  --nas-link: #0056b3;
  --nas-link-hover: #004494;
  --nas-product-blue: #0056b3;
  --nas-green-border: #00a651;
  --nas-row-label-width: 88px;
  --nas-row-label-color: #888888;
  /* 主行标题（芯存 XNAS / 机架式存储产品）与 SmartX 产品名同档 */
  --nas-section-head-size: 20px;
  --nas-section-desc-size: 14px;
  --nas-model-title-size: 15px;
  --nas-model-desc-size: 13px;

  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 2.25rem 1.5rem 4rem;
  box-sizing: border-box;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'PingFang SC',
    'Microsoft YaHei', sans-serif;
}

.nas-hero {
  text-align: left;
  margin-bottom: 1.75rem;
  max-width: 56rem;
}

.nas-hero-title {
  font-size: var(--nas-h1);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--nas-ink);
  margin: 0 0 14px;
  line-height: 1.2;
}

.nas-hero-lead {
  font-size: var(--nas-lead);
  line-height: 1.75;
  color: var(--nas-muted);
  max-width: 48rem;
  margin: 0;
  font-weight: 400;
}

.nas-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 2.5rem 2rem;
  align-items: start;
}

@media (max-width: 1024px) {
  .nas-layout {
    grid-template-columns: 1fr;
  }
}

.nas-main {
  min-width: 0;
}

.nas-featured {
  margin-bottom: 10px;
}

.nas-featured-inner {
  border: 1px solid var(--nas-green-border);
  border-radius: 6px;
  padding: 22px 26px;
  background: #fff;
}

.nas-featured-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--nas-product-blue);
  letter-spacing: -0.01em;
}

.nas-featured-sub {
  margin: 0 0 14px;
  font-size: var(--nas-small);
  font-weight: 400;
  line-height: 1.5;
  color: var(--nas-muted);
}

.nas-featured-desc {
  margin: 0;
  font-size: var(--nas-body);
  line-height: 1.75;
  color: var(--nas-muted);
}

.nas-aux-link {
  margin: 14px 0 2.25rem;
}

.nas-aux-link a {
  color: var(--nas-link);
  font-size: var(--nas-small);
  font-weight: 400;
  text-decoration: none;
}

.nas-aux-link a:hover {
  color: var(--nas-link-hover);
  text-decoration: underline;
}

.nas-row-block {
  display: grid;
  grid-template-columns: var(--nas-row-label-width) minmax(0, 1fr);
  column-gap: 32px;
  row-gap: 0;
  margin-bottom: 2.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #ececec;
  align-items: start;
}

.nas-row-block:last-of-type {
  border-bottom: none;
  padding-bottom: 0;
}

@media (max-width: 480px) {
  .nas-row-block {
    grid-template-columns: 1fr;
    align-items: start;
    margin-bottom: 2rem;
  }

  .nas-row-category {
    margin-bottom: 8px;
  }
}

.nas-row-category {
  font-size: 14px;
  font-weight: 400;
  color: var(--nas-row-label-color);
  line-height: 1.45;
  padding-top: 0;
  white-space: nowrap;
}

.nas-row-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  align-self: start;
}

.nas-row-head-link {
  display: block;
  text-decoration: none;
  font-size: inherit;
  line-height: inherit;
  /* 避免 a/visited 继承导致标题发灰、与静态「机架式存储产品」不一致 */
  color: var(--nas-product-blue);
}

.nas-row-head-link:hover .nas-row-head-name {
  color: var(--nas-link-hover);
  -webkit-text-fill-color: var(--nas-link-hover);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.nas-row-head-link:visited {
  color: var(--nas-product-blue);
  opacity: 1;
}

.nas-row-head-link:visited .nas-row-head-name {
  color: var(--nas-product-blue) !important;
  -webkit-text-fill-color: var(--nas-product-blue);
  opacity: 1;
}

.nas-row-head-link:visited .nas-row-head-desc {
  color: #666666 !important;
  -webkit-text-fill-color: #666666;
  opacity: 1;
}

.nas-row-head-static {
  display: block;
}

.nas-row-head-name {
  display: block;
  font-size: var(--nas-section-head-size);
  font-weight: 600;
  line-height: 1.35;
  color: var(--nas-product-blue);
  letter-spacing: -0.02em;
}

.nas-row-head-static .nas-row-head-name {
  font-size: var(--nas-section-head-size);
}

.nas-row-head-desc {
  margin: 8px 0 0;
  font-size: var(--nas-section-desc-size);
  line-height: 1.6;
  color: #666666;
  font-weight: 400;
}

.nas-product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nas-product-main {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

@media (min-width: 768px) {
  .nas-product-main.has-subbox {
    flex-direction: row;
    align-items: stretch;
    gap: 16px;
  }

  .nas-product-main.has-subbox .nas-product-block {
    flex: 1;
    min-width: 0;
  }

  .nas-product-main.has-subbox .nas-product-subbox {
    flex: 0 0 216px;
  }
}

.nas-product-block {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.nas-product-block:hover {
  border-color: #d1d5db;
  background: #fafafa;
}

.nas-product-link {
  display: block;
  /* 与上方「机架式存储产品」主标题左缘对齐 */
  padding: 14px 0;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  opacity: 1 !important;
  color: var(--nas-product-blue);
}

.nas-product-name {
  margin: 0 0 6px;
  font-size: var(--nas-model-title-size);
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.01em;
  color: #0056b3 !important;
  -webkit-text-fill-color: #0056b3;
  opacity: 1 !important;
}

.nas-product-link:visited {
  opacity: 1 !important;
}

.nas-product-link:visited .nas-product-name {
  color: #0056b3 !important;
  -webkit-text-fill-color: #0056b3;
  opacity: 1 !important;
}

.nas-product-link:hover .nas-product-name {
  color: var(--nas-link-hover) !important;
  -webkit-text-fill-color: var(--nas-link-hover);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.nas-product-desc {
  margin: 0;
  font-size: var(--nas-model-desc-size);
  line-height: 1.55;
  color: #666666 !important;
  -webkit-text-fill-color: #666666;
  opacity: 1 !important;
}

.nas-product-link:visited .nas-product-desc {
  color: #666666 !important;
  -webkit-text-fill-color: #666666;
  opacity: 1 !important;
}

.nas-product-subbox {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #f9fafb;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.nas-subitem-name {
  display: block;
  font-weight: 600;
  font-size: var(--nas-caption);
  color: var(--nas-product-blue);
}

.nas-subitem-hint {
  display: block;
  margin-top: 2px;
  color: var(--nas-muted);
  font-size: var(--nas-caption);
}

.nas-aside-card {
  position: sticky;
  top: 88px;
  padding: 1.25rem 1.2rem;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 48%);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  border: 1px solid #e8e8e8;
}

.nas-aside-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: var(--nas-link);
  background: rgba(0, 102, 204, 0.08);
  padding: 4px 10px;
  border-radius: 999px;
  margin-bottom: 10px;
}

.nas-aside-title {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 600;
  color: var(--nas-ink-soft);
}

.nas-aside-text {
  margin: 0 0 12px;
  font-size: var(--nas-caption);
  line-height: 1.65;
  color: var(--nas-muted);
}

.nas-aside-list {
  margin: 0 0 16px;
  padding-left: 1.05rem;
  font-size: var(--nas-caption);
  line-height: 1.6;
  color: var(--nas-label);
}

.nas-aside-cta {
  font-size: var(--nas-small);
  font-weight: 600;
  color: var(--nas-link);
  text-decoration: none;
}

.nas-aside-cta:hover {
  color: var(--nas-link-hover);
  text-decoration: underline;
}
</style>
